import { useQuery, useMutation } from "@tanstack/react-query";
import { Users, BookOpen, MessageSquare, TrendingUp, Plus, Trash2, UserPlus, Upload, Edit, Pencil } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useLocation } from "wouter";
import { useState, useEffect } from "react";



export default function AdminDashboard() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  
  // Pagination state for User Management section
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [userToDelete, setUserToDelete] = useState<any>(null);

  const { data: modules } = useQuery({
    queryKey: ['/training-modules'],
  });

  const { data: feedback } = useQuery({
    queryKey: ['/feedback'],
  });

  const { data: users, isLoading: usersLoading, error: usersError } = useQuery({
    queryKey: ['/admin/users'],
    queryFn: async () => {
      const response = await apiRequest("GET", "/admin/users");
      return await response.json();
    },
  });


  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await apiRequest("DELETE", `/admin/users/${userId}`);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "User deleted successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['/admin/users'] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteModuleMutation = useMutation({
    mutationFn: async (moduleId: string) => {
      const response = await apiRequest("DELETE", `/training-modules/${moduleId}`);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Module deleted successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['/training-modules'] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete module",
        variant: "destructive",
      });
    },
  });

  // Admin stats
  const stats = {
    totalEmployees: (users as any)?.users?.length || 0,
    activeModules: (modules as any)?.modules?.length || 0,
    pendingFeedback: (feedback as any)?.feedback?.length || 0,
    avgCompletion: 78,
  };

  // Pagination logic for User Management
  const usersList = (users as any)?.users || [];
  const totalPages = Math.ceil(usersList.length / itemsPerPage);
  
  // Clamp current page when user list changes
  useEffect(() => {
    if (usersList.length > 0 && totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    } else if (usersList.length === 0) {
      setCurrentPage(1);
    }
  }, [usersList.length, totalPages, currentPage]);
  
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = usersList.slice(startIndex, endIndex);

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Admin Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-testid="admin-stats-overview">
        <Card className="border-servify-white-grey shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-servify-purple/10 rounded-lg flex items-center justify-center">
                <Users className="text-servify-purple h-6 w-6" />
              </div>
              <span className="text-2xl font-bold text-servify-purple" data-testid="text-total-employees">{stats.totalEmployees}</span>
            </div>
            <h3 className="font-semibold text-servify-dark-grey mb-1">Total Employees</h3>
            <p className="text-servify-dark-grey/70 text-sm">In induction program</p>
          </CardContent>
        </Card>
        
        <Card className="border-servify-white-grey shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-servify-purple/10 rounded-lg flex items-center justify-center">
                <BookOpen className="text-servify-purple h-6 w-6" />
              </div>
              <span className="text-2xl font-bold text-servify-purple" data-testid="text-active-modules">{stats.activeModules}</span>
            </div>
            <h3 className="font-semibold text-servify-dark-grey mb-1">Active Modules</h3>
            <p className="text-servify-dark-grey/70 text-sm">Training content</p>
          </CardContent>
        </Card>

        <Card className="border-servify-white-grey shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-servify-purple/10 rounded-lg flex items-center justify-center">
                <MessageSquare className="text-servify-purple h-6 w-6" />
              </div>
              <span className="text-2xl font-bold text-servify-purple" data-testid="text-pending-feedback">{stats.pendingFeedback}</span>
            </div>
            <h3 className="font-semibold text-servify-dark-grey mb-1">Feedback Items</h3>
            <p className="text-servify-dark-grey/70 text-sm">Awaiting review</p>
          </CardContent>
        </Card>

        <Card className="border-servify-white-grey shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-servify-purple/10 rounded-lg flex items-center justify-center">
                <TrendingUp className="text-servify-purple h-6 w-6" />
              </div>
              <span className="text-2xl font-bold text-servify-purple" data-testid="text-avg-completion">{stats.avgCompletion}%</span>
            </div>
            <h3 className="font-semibold text-servify-dark-grey mb-1">Average Completion</h3>
            <p className="text-servify-dark-grey/70 text-sm">Across all modules</p>
          </CardContent>
        </Card>
      </div>
      
      {/* User Management Section */}
      <Card className="border-servify-white-grey shadow-lg" data-testid="user-management-section">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            User Management
          </CardTitle>
          <div className="flex gap-2">
            <Button 
              size="sm" 
              variant="outline"
              data-testid="button-bulk-upload"
              onClick={() => setLocation('/bulk-upload')}
            >
              <Upload className="mr-2 h-4 w-4" />
              Bulk Upload
            </Button>
            <Button 
              size="sm" 
              data-testid="button-manage-add-candidate"
              onClick={() => setLocation('/add-candidate')}
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Add Candidate
            </Button>
          </div>

        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {usersError && (
              <div className="text-servify-red p-4 border border-servify-red/30 rounded">
                Error loading users: {(usersError as any)?.message || 'Unknown error'}
              </div>
            )}
            {usersLoading && (
              <div className="text-center py-8">Loading users...</div>
            )}
            {currentUsers.length > 0 && currentUsers.map((user: any) => (
                              <div
                  key={user.id}
                  className="flex items-center justify-between p-4 border border-servify-white-grey rounded-lg"
                  data-testid={`user-item-${user.id}`}
                >
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-servify-purple rounded-full flex items-center justify-center text-white font-semibold">
                    {user.firstName?.[0]}{user.lastName?.[0]}
                  </div>
                  <div>
                    <h4 className="font-semibold text-servify-dark-grey">{user.firstName} {user.lastName}</h4>
                    <p className="text-sm text-servify-dark-grey/70">{user.email}</p>
                    <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                      user.role === 'admin' ? 'bg-servify-purple/20 text-servify-purple' :
                      user.role === 'employee' ? 'bg-servify-purple/20 text-servify-purple' :
                      'bg-servify-purple/20 text-servify-purple'
                    }`}>
                      {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                    </span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setUserToDelete(user)}
                  disabled={deleteUserMutation.isPending}
                  data-testid={`button-delete-user-${user.id}`}
                >
                  <Trash2 className="h-4 w-4 text-servify-red" />
                </Button>
              </div>
            ))}
            {!usersLoading && usersList.length === 0 && (
              <p className="text-center text-servify-dark-grey/50 py-8">No users found.</p>
            )}

            {/* Pagination Controls */}
            {usersList.length > 0 && totalPages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t border-servify-white-grey">
                <div className="text-sm text-servify-dark-grey/70">
                  Showing {startIndex + 1} to {Math.min(endIndex, usersList.length)} of {usersList.length} users
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    data-testid="button-prev-page"
                  >
                    Previous
                  </Button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                        className={currentPage === page ? "bg-servify-purple text-white" : ""}
                        data-testid={`button-page-${page}`}
                      >
                        {page}
                      </Button>
                    ))}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    data-testid="button-next-page"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className="border-servify-white-grey shadow-lg" data-testid="training-modules-management">
          <CardHeader className="border-b border-servify-white-grey">
                          <CardTitle>Knowledge Hub Management</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {(modules as any)?.modules?.map((module: any) => (
                <div key={module.id} className="flex items-center justify-between p-3 bg-servify-white-grey rounded-lg" data-testid={`admin-module-${module.id}`}>
                  <div>
                    <h3 className="font-medium text-servify-dark-grey" data-testid={`text-admin-module-title-${module.id}`}>
                      {module.title}
                    </h3>
                    <p className="text-sm text-servify-dark-grey/70">
                      {module.duration} minutes • Created {new Date(module.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setLocation(`/add-training-module?edit=${module.id}`)}
                      data-testid={`button-edit-module-${module.id}`}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        if (confirm(`Are you sure you want to delete "${module.title}"? This action cannot be undone.`)) {
                          deleteModuleMutation.mutate(module.id);
                        }
                      }}
                      disabled={deleteModuleMutation.isPending}
                      data-testid={`button-delete-module-${module.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-servify-red" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            
            <Button 
              className="w-full mt-4 bg-servify-purple hover:bg-servify-purple-dark text-white"
              onClick={() => setLocation('/training')}
              data-testid="button-manage-all-modules"
            >
              <Plus className="mr-2 h-4 w-4" />
                              Manage Knowledge Hub
            </Button>
          </CardContent>
        </Card>

        <Card className="border-servify-white-grey shadow-lg" data-testid="recent-feedback">
          <CardHeader className="border-b border-servify-white-grey">
            <CardTitle>Recent Feedback</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {(feedback as any)?.feedback && Array.isArray((feedback as any).feedback) && (feedback as any).feedback.length > 0 ? (
                (feedback as any).feedback.slice(0, 3).map((item: any) => (
                  <div key={item.id} className="p-3 bg-servify-white-grey rounded-lg" data-testid={`feedback-item-${item.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <span 
                            key={i} 
                            className={`text-sm ${i < item.rating ? 'text-servify-purple' : 'text-servify-dark-grey/30'}`}
                          >
                            ★
                          </span>
                        ))}
                      </div>
                      <span className="text-xs text-servify-dark-grey/50" data-testid={`text-feedback-date-${item.id}`}>
                        {new Date(item.submittedAt).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-servify-dark-grey" data-testid={`text-feedback-comment-${item.id}`}>
                      {item.comments || "No comments provided"}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-servify-dark-grey/50 text-center py-4" data-testid="text-no-feedback">
                  No feedback submitted yet
                </p>
              )}
            </div>
            
            <Button 
              variant="outline" 
              className="w-full mt-4 border-servify-purple text-servify-purple hover:bg-servify-purple hover:text-white"
              onClick={() => setLocation('/feedback')}
              data-testid="button-view-all-feedback"
            >
              View All Feedback
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!userToDelete} onOpenChange={(open) => !open && setUserToDelete(null)}>
        <AlertDialogContent className="bg-white border-2 border-gray-300 shadow-2xl max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-bold text-gray-900">Delete User</AlertDialogTitle>
            <AlertDialogDescription className="text-base text-gray-700 mt-3 leading-relaxed">
              Are you sure you want to delete <span className="font-semibold text-gray-900">{userToDelete?.firstName} {userToDelete?.lastName}</span>? This action cannot be undone and will permanently remove all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-6">
            <AlertDialogCancel 
              onClick={() => setUserToDelete(null)}
              className="bg-gray-100 hover:bg-gray-200 text-gray-800 border-2 border-gray-300 font-semibold px-6"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (userToDelete) {
                  deleteUserMutation.mutate(userToDelete.id);
                  setUserToDelete(null);
                }
              }}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 shadow-lg"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
